import {
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type FocusEvent,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import './Tooltip.scss';

export type TooltipPlacement = 'top' | 'bottom' | 'left' | 'right';

interface TooltipProps {
  /** Tooltip body. String for one-liners; ReactNode for multi-line / formatted. */
  label: ReactNode;
  /** Preferred side. Auto-flips to the opposite side when there isn't enough
   *  room in the viewport so the tooltip is always fully visible. */
  placement?: TooltipPlacement;
  /** Delay before the tooltip appears on hover. Mirrors macOS/Linear feel
   *  (300ms) — fast enough to feel responsive, slow enough that sweeping the
   *  cursor across a toolbar doesn't fire a strip of tooltips. */
  delay?: number;
  /** Skip rendering entirely. Useful when the label would be empty or when
   *  the trigger is in a state where a tooltip would be noisy (e.g., during
   *  a drag). */
  disabled?: boolean;
  /** Single React element used as the trigger. We clone it to attach
   *  hover/focus listeners + aria-describedby. */
  children: ReactElement;
}

const VIEWPORT_PAD = 8;       // px breathing room from viewport edges
const TRIGGER_GAP = 8;        // px between trigger and tooltip
const HIDE_ON_SCROLL = true;  // close when user scrolls — position would otherwise drift

/**
 * Hover/focus tooltip that portals into <body> so it can't be clipped by
 * overflow:hidden parents (table cells, modals, scrollable rails). Designed
 * to be a tiny drop-in replacement for the browser's native `title=""`:
 *
 *   <Tooltip label="Delete">
 *     <button><Trash size={14} /></button>
 *   </Tooltip>
 *
 * Accessibility:
 *   - Tooltip body has role="tooltip" + a unique id.
 *   - Trigger gets `aria-describedby` linking to the tooltip when shown.
 *   - Hover, focus, and Escape are all wired — keyboard users see the same
 *     hint as mouse users.
 */
export const Tooltip = ({
  label,
  placement = 'top',
  delay = 300,
  disabled = false,
  children,
}: TooltipProps) => {
  const id = useId();
  const triggerRef = useRef<HTMLElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const showTimerRef = useRef<number | null>(null);

  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{
    top: number;
    left: number;
    side: TooltipPlacement;
  } | null>(null);

  const clearTimer = useCallback(() => {
    if (showTimerRef.current !== null) {
      window.clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    }
  }, []);

  const show = useCallback(() => {
    if (disabled || label === null || label === undefined || label === '') return;
    clearTimer();
    if (delay <= 0) {
      setOpen(true);
      return;
    }
    showTimerRef.current = window.setTimeout(() => setOpen(true), delay);
  }, [delay, disabled, label, clearTimer]);

  const hide = useCallback(() => {
    clearTimer();
    setOpen(false);
  }, [clearTimer]);

  // Cleanup outstanding timers on unmount so we don't open a stale tooltip
  // after the trigger is gone.
  useEffect(() => () => clearTimer(), [clearTimer]);

  // Position the tooltip once the DOM has its measurements. Using layout
  // effect (not effect) avoids a single-frame flash at the (0,0) origin
  // before the position is computed.
  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    const trigger = triggerRef.current;
    const tip = tooltipRef.current;
    if (!trigger || !tip) return;

    const t = trigger.getBoundingClientRect();
    const tw = tip.offsetWidth;
    const th = tip.offsetHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // Try preferred side first. If it overflows the viewport, flip to the
    // opposite side. Doesn't try perpendicular flips (top→left etc) — for
    // our usage that's never the right answer; keep behavior predictable.
    const computeFor = (side: TooltipPlacement) => {
      let top = 0;
      let left = 0;
      switch (side) {
        case 'top':
          top = t.top - th - TRIGGER_GAP;
          left = t.left + t.width / 2 - tw / 2;
          break;
        case 'bottom':
          top = t.bottom + TRIGGER_GAP;
          left = t.left + t.width / 2 - tw / 2;
          break;
        case 'left':
          top = t.top + t.height / 2 - th / 2;
          left = t.left - tw - TRIGGER_GAP;
          break;
        case 'right':
          top = t.top + t.height / 2 - th / 2;
          left = t.right + TRIGGER_GAP;
          break;
      }
      return { top, left };
    };

    const fits = (side: TooltipPlacement) => {
      const { top, left } = computeFor(side);
      return (
        top >= VIEWPORT_PAD &&
        left >= VIEWPORT_PAD &&
        top + th <= vh - VIEWPORT_PAD &&
        left + tw <= vw - VIEWPORT_PAD
      );
    };

    const opposite: Record<TooltipPlacement, TooltipPlacement> = {
      top: 'bottom',
      bottom: 'top',
      left: 'right',
      right: 'left',
    };

    const finalSide = fits(placement) ? placement : opposite[placement];
    const { top, left } = computeFor(finalSide);

    // After picking a side, still clamp the cross-axis so a corner-anchored
    // trigger near the viewport edge doesn't push the tooltip off-screen
    // (e.g., a tooltip on a button at the right edge of the screen).
    const clampedLeft = Math.max(
      VIEWPORT_PAD,
      Math.min(left, vw - tw - VIEWPORT_PAD),
    );
    const clampedTop = Math.max(
      VIEWPORT_PAD,
      Math.min(top, vh - th - VIEWPORT_PAD),
    );

    setPos({ top: clampedTop, left: clampedLeft, side: finalSide });
  }, [open, placement]);

  // Global listeners while open: Escape to dismiss, scroll/resize to close
  // (re-hover restores). Using the capture phase so we beat any
  // stopPropagation on the page.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') hide();
    };
    document.addEventListener('keydown', onKey, true);
    if (HIDE_ON_SCROLL) {
      window.addEventListener('scroll', hide, true);
      window.addEventListener('resize', hide);
    }
    return () => {
      document.removeEventListener('keydown', onKey, true);
      if (HIDE_ON_SCROLL) {
        window.removeEventListener('scroll', hide, true);
        window.removeEventListener('resize', hide);
      }
    };
  }, [open, hide]);

  if (!isValidElement(children)) {
    // Tooltip without a valid child element can't anchor itself — return the
    // children untouched rather than throwing, so the rest of the page still
    // renders. This is the same forgiving behavior Radix/Headless use.
    return children;
  }

  // Compose event handlers with anything the child already had — never
  // swallow them. The cast through `as any` is because TS can't infer the
  // element type from a generic ReactElement at this point.
  type HandlersOnChild = {
    onMouseEnter?: (e: MouseEvent) => void;
    onMouseLeave?: (e: MouseEvent) => void;
    onFocus?: (e: FocusEvent) => void;
    onBlur?: (e: FocusEvent) => void;
    onClick?: (e: MouseEvent) => void;
    ref?: React.Ref<HTMLElement>;
  };
  const childProps = (children.props ?? {}) as HandlersOnChild;

  const trigger = cloneElement(children, {
    ref: (node: HTMLElement | null) => {
      triggerRef.current = node;
      // Forward the ref if the child already had one. Supports both function
      // refs and ref objects.
      const childRef = (children as unknown as { ref?: React.Ref<HTMLElement> }).ref;
      if (typeof childRef === 'function') childRef(node);
      else if (childRef && typeof childRef === 'object')
        (childRef as { current: HTMLElement | null }).current = node;
    },
    onMouseEnter: (e: MouseEvent) => {
      childProps.onMouseEnter?.(e);
      show();
    },
    onMouseLeave: (e: MouseEvent) => {
      childProps.onMouseLeave?.(e);
      hide();
    },
    onFocus: (e: FocusEvent) => {
      childProps.onFocus?.(e);
      show();
    },
    onBlur: (e: FocusEvent) => {
      childProps.onBlur?.(e);
      hide();
    },
    onClick: (e: MouseEvent) => {
      childProps.onClick?.(e);
      // Hide on click — otherwise the tooltip stays visible after a button
      // action which feels glitchy, especially when the button removes
      // itself from the DOM (modal triggers, delete buttons).
      hide();
    },
    'aria-describedby': open ? id : (children.props as { 'aria-describedby'?: string })?.['aria-describedby'],
  } as Partial<HandlersOnChild> & { 'aria-describedby'?: string });

  return (
    <>
      {trigger}
      {open &&
        createPortal(
          <div
            ref={tooltipRef}
            id={id}
            role="tooltip"
            className={
              'pw-tooltip' +
              (pos ? ` pw-tooltip--${pos.side}` : ' pw-tooltip--measuring')
            }
            style={
              pos
                ? { top: pos.top, left: pos.left }
                : // Render once off-screen so we can measure before flipping in
                  { top: -9999, left: -9999, opacity: 0 }
            }
          >
            {label}
          </div>,
          document.body,
        )}
    </>
  );
};
